import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Input, Select, Alert, Badge } from '@components';
import { AutoRechargeSettings as AutoRechargeSettingsType, PaymentMethod } from '../types';

const ERROR_AUTO_DISMISS_TIME = 5000; // 5 seconds

interface AutoRechargeSettingsProps {
  settings: AutoRechargeSettingsType | null;
  paymentMethods: PaymentMethod[];
  loading?: boolean;
  onSave: (settings: {
    status: 'enabled' | 'disabled';
    amount: string;
    threshold: string;
    payment_method?: string;
  }) => void;
  error?: string | null;
  onClearError?: () => void;
}

const AUTO_RECHARGE_MIN_AMOUNT = 50;
const AUTO_RECHARGE_MAX_AMOUNT = 500;
const AUTO_RECHARGE_DEFAULT_THRESHOLD = 20.0;

export const AutoRechargeSettings: React.FC<AutoRechargeSettingsProps> = ({
  settings,
  paymentMethods,
  loading = false,
  onSave,
  error = null,
  onClearError,
}) => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = useState(settings?.status === 'enabled');
  const [amount, setAmount] = useState(settings?.amount || '50');
  const [threshold, setThreshold] = useState(
    settings?.threshold || String(AUTO_RECHARGE_DEFAULT_THRESHOLD)
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showError, setShowError] = useState(false);

  const capitalizeBrand = (brand?: string) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
  };

  useEffect(() => {
    if (settings) {
      setIsEnabled(settings.status === 'enabled');
      setAmount(settings.amount || '50');
      setThreshold(settings.threshold || String(AUTO_RECHARGE_DEFAULT_THRESHOLD));
    }
  }, [settings]);

  useEffect(() => {
    if (paymentMethods.length > 0) {
      if (settings?.payment_method) {
        const paymentMethodId =
          typeof settings.payment_method === 'object' && settings.payment_method !== null
            ? settings.payment_method.id
            : String(settings.payment_method);

        const exists = paymentMethods.some(pm => pm.id === paymentMethodId);
        if (exists) {
          setSelectedPaymentMethod(paymentMethodId);
          return;
        }
      }

      if (!selectedPaymentMethod) {
        const activePaymentMethods = paymentMethods.filter(pm => pm.is_active);
        if (activePaymentMethods.length > 0) {
          const defaultMethod = activePaymentMethods.find(pm => pm.is_default);
          if (defaultMethod) {
            setSelectedPaymentMethod(defaultMethod.id);
          } else {
            setSelectedPaymentMethod(activePaymentMethods[0].id);
          }
        }
      }
    }
  }, [settings, paymentMethods, selectedPaymentMethod]);

  useEffect(() => {
    if (error) {
      setShowError(true);
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        setShowError(false);
      }, ERROR_AUTO_DISMISS_TIME);
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [error]);

  useEffect(() => {
    if (loading) {
      setShowError(false);
    }
  }, [loading]);

  const handleDismissError = () => {
    setShowError(false);
    onClearError?.();
  };

  const handleSave = () => {
    const amountNum = parseFloat(amount);
    const thresholdNum = parseFloat(threshold);

    if (isEnabled) {
      if (amountNum < AUTO_RECHARGE_MIN_AMOUNT || amountNum > AUTO_RECHARGE_MAX_AMOUNT) {
        return;
      }
      if (thresholdNum <= 0) {
        return;
      }
      if (!selectedPaymentMethod) {
        return;
      }
    }

    setShowError(false);
    onSave({
      status: isEnabled ? 'enabled' : 'disabled',
      amount: amount,
      threshold: threshold,
      payment_method: isEnabled && selectedPaymentMethod ? selectedPaymentMethod : undefined,
    });
  };

  const activePaymentMethods = paymentMethods.filter(pm => pm.is_active);
  const amountNum = parseFloat(amount);
  const thresholdNum = parseFloat(threshold);

  const amountError =
    isEnabled && (amountNum < AUTO_RECHARGE_MIN_AMOUNT || amountNum > AUTO_RECHARGE_MAX_AMOUNT)
      ? `Amount must be between €${AUTO_RECHARGE_MIN_AMOUNT} and €${AUTO_RECHARGE_MAX_AMOUNT}`
      : null;

  const thresholdError = isEnabled && thresholdNum <= 0 ? 'Threshold must be greater than 0' : null;

  const paymentMethodError =
    isEnabled && activePaymentMethods.length > 0 && !selectedPaymentMethod
      ? t('billing.pleaseSelectPaymentMethod')
      : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('billing.autoRechargeSettings')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('billing.autoRechargeDescription')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isEnabled ? 'success' : 'secondary'} size="sm">
            {isEnabled ? t('agents.enabled') : t('agents.disabled')}
          </Badge>
        </div>
      </div>

      {error && showError && (
        <Alert variant="error" title="Error" dismissible onDismiss={handleDismissError}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {t('billing.autoRecharge')}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('billing.autoRechargeEnabled')}
                </div>
              </div>
              <label
                htmlFor="auto-recharge-toggle"
                className="relative inline-flex items-center cursor-pointer"
              >
                <input
                  id="auto-recharge-toggle"
                  type="checkbox"
                  checked={isEnabled}
                  onChange={e => setIsEnabled(e.target.checked)}
                  disabled={loading}
                  className="sr-only peer"
                  aria-label="Enable auto-recharge"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                <span className="sr-only">Toggle auto-recharge</span>
              </label>
            </div>

            {isEnabled && (
              <>
                {/* Amount Selector */}
                <div>
                  <label
                    htmlFor="recharge-amount"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('billing.rechargeAmount')} (€{AUTO_RECHARGE_MIN_AMOUNT} - €
                    {AUTO_RECHARGE_MAX_AMOUNT})
                  </label>
                  <Input
                    id="recharge-amount"
                    type="number"
                    min={AUTO_RECHARGE_MIN_AMOUNT}
                    max={AUTO_RECHARGE_MAX_AMOUNT}
                    step="10"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    disabled={loading}
                    className="w-full"
                    placeholder="50"
                  />
                  {amountError && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{amountError}</p>
                  )}
                </div>

                {/* Threshold Input */}
                <div>
                  <label
                    htmlFor="recharge-threshold"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('billing.rechargeThreshold')} (€)
                  </label>
                  <Input
                    id="recharge-threshold"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={threshold}
                    onChange={e => setThreshold(e.target.value)}
                    disabled={loading}
                    className="w-full"
                    placeholder="20.00"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {t('billing.rechargeWillTrigger')}
                  </p>
                  {thresholdError && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{thresholdError}</p>
                  )}
                </div>

                {/* Payment Method Selection */}
                <div>
                  <label
                    htmlFor="payment-method"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('billing.paymentMethod')} *
                  </label>
                  {activePaymentMethods.length === 0 ? (
                    <Alert variant="warning" title={t('billing.noPaymentMethods')}>
                      {t('billing.pleaseAddPaymentMethodAutoRecharge')}
                    </Alert>
                  ) : (
                    <Select
                      id="payment-method"
                      value={selectedPaymentMethod}
                      onChange={value => {
                        const stringValue = Array.isArray(value) ? value[0] : value;
                        setSelectedPaymentMethod(stringValue);
                      }}
                      disabled={loading}
                      options={[
                        { value: '', label: t('billing.selectPaymentMethod') },
                        ...activePaymentMethods.map(pm => ({
                          value: pm.id,
                          label: `${capitalizeBrand(pm.brand || pm.card_brand)} ${t('billing.cardEndingIn')} ${pm.last4 || pm.card_last4 || '****'} ${
                            pm.is_default ? `(${t('billing.default')})` : ''
                          }`,
                        })),
                      ]}
                      className="w-full"
                    />
                  )}
                  {paymentMethodError && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {paymentMethodError}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Save Button */}
            <Button
              variant="gradient-multi"
              onClick={handleSave}
              disabled={
                loading || !!amountError || !!thresholdError || (isEnabled && !!paymentMethodError)
              }
              className="w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="material-icons animate-spin">refresh</span>
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <span className="material-icons">save</span>
                  {t('common.saveChanges')}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
